import { useQuery } from "@tanstack/react-query";
import { ArrowLeftRight, Check } from "lucide-react";
import { useApi, type CaseEvent } from "@ncp/api-client";
import { formatDateTime, useLocale, useTranslation } from "@ncp/i18n";
import { Card, SourceText } from "@ncp/ui";
import type { Phase } from "../process/phases";
import { ReviewHistory } from "../review/ReviewHistory";
import { useActorName } from "../../../lib/staff-names";

/**
 * A stage the file has passed, read-only: its steps, how each one was left (the decision taken, by whom and when), and the stage reviews
 * concluded in it – findings, rationale and signature.
 */
export function PhaseRecord({ caseId, phase }: { caseId: string; phase: Phase }) {
  const { t } = useTranslation();
  const actorName = useActorName();
  const { locale } = useLocale();
  const api = useApi();
  const ar = locale === "ar";
  const keys = phase.stages.map((s) => s.node.key);
  const events = useQuery({ queryKey: ["case-events", caseId], queryFn: () => api.get<CaseEvent[]>(`/api/cases/${caseId}/workflow/events`) });
  const exits = (events.data ?? []).filter((e) => e.kind === "StateChanged" && e.fromState && keys.includes(e.fromState)).sort((a, b) => a.occurredAt.localeCompare(b.occurredAt));
  const passed = phase.stages.filter((s) => s.standing === "visited" || s.standing === "current");
  return (
    <div className="space-y-4">
      <Card className="px-4 py-3">
        <h2 className="font-heading text-base font-semibold text-aeblack-950">{t("backoffice:stage.recordOf", { phase: t(`backoffice:workspace.phases.${phase.key}`) })}</h2>
        <p className="mt-0.5 text-sm text-sand-800">{t("backoffice:stage.recordHint")}</p>
        {passed.length > 0 ? (
          <ol className="mt-3 flex flex-wrap gap-2">
            {passed.map((s) => <li key={s.node.key} className="inline-flex items-center gap-1.5 rounded-full border border-success-200 bg-success-50 px-2.5 py-1 text-xs text-success-900"><Check className="size-3.5" aria-hidden /> {ar ? s.node.labelAr : s.node.label}</li>)}
          </ol>
        ) : null}
        {exits.length > 0 ? (
          <ol className="mt-4 space-y-3 border-t border-sand-100 pt-3" aria-label={t("backoffice:stage.recordExits")}>
            {exits.map((e) => (
              <li key={e.id} className="flex items-start gap-3">
                <ArrowLeftRight className="mt-0.5 size-4 shrink-0 text-aegold-700" aria-hidden />
                <div className="min-w-0 flex-1">
                  <p className="text-sm text-aeblack-900">{ar ? e.titleAr ?? e.title : e.title}</p>
                  {(ar ? e.detailAr : e.detail) ? <p className="mt-0.5 text-xs text-sand-800" dir="auto">{ar ? e.detailAr : e.detail}</p> : null}
                  <p className="mt-0.5 text-xs text-sand-800">{e.actorKind === "Human" && e.actorName && e.actorName !== "system" ? <SourceText text={actorName(e)} /> : t(`backoffice:stage.actor.${e.actorKind === "Human" ? "System" : e.actorKind}`)} · {formatDateTime(e.occurredAt, locale)}</p>
                </div>
              </li>
            ))}
          </ol>
        ) : null}
      </Card>
      <ReviewHistory caseId={caseId} stages={keys} emptyTitle={t("backoffice:stage.recordEmpty")} />
    </div>
  );
}
